// /frontend/src/App.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import AisaTable from "./AisaTable";
import HelpModal from "./HelpModal";
import ColumnConfigurator from './ColumnConfigurator';
import './ChatInterface.css';
import { FaBars, FaChevronLeft, FaChevronRight, FaGlobe, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import CandidateEditModal from './CandidateEditModal';
import PortalNavbar from './PortalNavbar';

// Lista dei comandi che attivano l'aggiunta dei filtri rapidi
const KNOWN_INTENTS = ["aisafit", "aisachk", "aisastat", "aisafeed", "aisadeep", "aisarep", "cerca:"]; // Aggiunto cerca:

const API_URL = "";

// Funzione che controlla se il testo del messaggio contiene uno dei comandi
// NOTA: Questa funzione detectIntent NON viene usata per la logica dei filtri,
// ma potrebbe essere usata altrove o rimossa se non necessaria.
function detectIntent(prompt) {
  if (!prompt) return null;
  const p = prompt.toLowerCase().trim();
  for (const intent of KNOWN_INTENTS) {
    // Usa startsWith per essere più preciso
    if (p.startsWith(intent)) {
      return intent;
    }
  }
  // Aggiungi controllo per "mandatorio:"
  if (p.includes("mandatorio:")) {
    // Potrebbe essere un comando NLU generico con filtri
    // Ritorniamo un placeholder o null a seconda di come vuoi gestirlo
    return "command_with_filters"; // O null
  }
  return null;
}


// Funzioni di formattazione (invariate)
function toText(v) {
  if (v == null) return "";
  if (typeof v === "string") {
    try {
      return JSON.stringify(JSON.parse(v), null, 2);
    } catch (e) { return v; }
  }
  return JSON.stringify(v, null, 2);
}

function formatCellDisplayValue(value) {
  if (value === null || value === undefined || value === false || value === 0) {
    return "";
  }
  return String(value);
}

function sanitizeAndParseJson(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }
  let cleanString = value.trim().replace(/'/g, '"');
  if (cleanString.startsWith('[') && cleanString.endsWith(']')) {
    try { return JSON.parse(cleanString); } catch (e) { /* ignore */ }
  }
  // Modifica per gestire JSON non in array
  if (cleanString.startsWith('{') && cleanString.endsWith('}')) {
    try { return JSON.parse(cleanString); } catch (e) { /* ignore */ }
  }
  // Fallback per JSON multipli separati da virgola (ma non array)
  if (cleanString.includes('{')
    && cleanString.includes('}')) {
    try { return JSON.parse(`[${cleanString}]`); } catch (e) { /* ignore */ }
  }
  return value;
}


function formatYearSmartly(dateString) {
  if (!dateString || typeof dateString !== 'string') return dateString;
  // Converte anni a 2 cifre solo se preceduti da spazio, '/', '-' o inizio stringa
  return dateString.replace(/(^|\s|\/|-)(\d{2})\b/g, (match, p1, p2) => `${p1}20${p2}`);
}

function prettifyEsperienze(value) {
  const data = sanitizeAndParseJson(value);
  if (!Array.isArray(data)) return toText(value); // Assicura che sia un array
  const fmtOne = (it) => {
    if (!it || typeof it !== "object") return toText(it);
    const azienda = it.azienda ?? it.company ?? "";
    let periodo = formatYearSmartly(it.periodo ?? it.date ?? "");
    const ruolo = it.ruolo ?? it.title ?? "";
    const parts = [azienda, ruolo, periodo].filter(Boolean);
    return parts.join(" · ");
  };
  return data.map(fmtOne).join("\n— "); // Usa direttamente l'array
}

function prettifyIstruzione(value) {
  const data = sanitizeAndParseJson(value);
  if (!Array.isArray(data)) return toText(value);
  // Assicura che sia un array
  const fmtOne = (it) => {
    if (!it || typeof it !== "object") return toText(it);
    const titolo = it.titolo ?? it.corso ?? "";
    const istituto = it.istituto ?? it.ente ?? "";
    let anno = formatYearSmartly(it.anno ?? it.year ?? "");
    const parts = [titolo, istituto, anno].filter(Boolean);
    return parts.join(" · ");
  };
  return data.map(fmtOne).join("\n— "); // Usa direttamente l'array
}

function prettifyGenericJsonObject(value) {
  const data = sanitizeAndParseJson(value);
  // Gestisce sia array di oggetti che oggetto singolo
  const dataArray = Array.isArray(data) ?
    data : (typeof data === 'object' && data !== null ? [data] : null);
  if (dataArray) {
    const formatItem = (item) => {
      if (typeof item !== 'object' || item === null) return toText(item);
      return Object.entries(item)
        .filter(([, val]) => val !== null && val !== undefined && String(val).trim() !== '') // Filtra valori nulli/vuoti
        .map(([key, val]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${formatYearSmartly(String(val))}`)
        .join(' · ');
    };
    return dataArray.map(formatItem).join('\n— ');
  }
  // Se non è un oggetto/array JSON, gestisce stringhe semplici
  if (typeof value === 'string') {
    // Tentativo di formattare stringhe che sembrano JSON ma non lo sono
    if (value.includes(':') && (value.includes(',') || value.includes('{'))) {
      return value.replace(/}, {/g, '}\n— {').replace(/['"{}]/g, '').replace(/, /g, ' · ');
    }
    return value; // Restituisce la stringa semplice
  }
  return toText(value); // Fallback
}


function prettifyDataItaliana(value) {
  if (!value || typeof value !== 'string')
    return value;
  let date;
  // Regex migliorata per T Z e millisecondi
  const isoDateRegex = /^(\d{4}-\d{2}-\d{2})[T ]?(\d{2}:\d{2}:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  const italianDateRegex = /^(\d{2})\/(\d{2})\/(\d{4})/;

  if (value.match(isoDateRegex)) {
    // Prova a parsare come ISO 8601
    date = new Date(value);
  } else {
    // Prova a parsare come DD/MM/YYYY
    const match = value.match(italianDateRegex);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // Mesi sono 0-indexed
      const year = parseInt(match[3], 10);
      // Controllo validità data italiana
      const tempDate = new Date(year, month, day);
      if (tempDate.getFullYear() === year && tempDate.getMonth() === month && tempDate.getDate() === day) {
        date = tempDate;
      } else {
        date = new Date(NaN);
        // Data non valida
      }
    } else {
      // Fallback: prova a parsare direttamente, potrebbe funzionare per YYYY-MM-DD
      date = new Date(value);
    }
  }

  // Controlla se la data è valida e non è l'epoch (spesso usato come default)
  if (isNaN(date.getTime()) || date.getTime() === 0) {
    return value;
    // Restituisce la stringa originale se non valida o epoch
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Mesi sono 1-indexed per display
  const year = date.getFullYear();
  // Restituisce formattato solo se l'anno è ragionevole
  return year > 1900 ? `${day}/${month}/${year}` : value;
}


function formatCellForUI(label, value) {
  const L = String(label || "").toLowerCase();
  // Usa regex più specifiche per date
  if (/\b(data|aggiornamento|disponibilità|nascita|dal|al)\b/i.test(L)) return prettifyDataItaliana(value);
  if (/(esperienz|work\s*exper|lavorativ)/i.test(L)) return prettifyEsperienze(value);
  if (/(istruz|educa|formaz|education)/i.test(L)) return prettifyIstruzione(value);
  // Fallback per altri oggetti/stringhe potenzialmente JSON
  return prettifyGenericJsonObject(value);
}


function prepareTableForUI(table, context, columnConfig, setColumnConfig) {
  // ... (Logica invariata per gestire la configurazione colonne e formattazione righe) ...
  if (!table || !table.columns || !table.rows) {
    return { columns: [], rows: [] };
  }
  let currentConfig = Array.isArray(columnConfig) ? [...columnConfig] : [];
  const rawCols = table.columns;
  let newColumnsFound = false;
  const effectiveContext = context || 'global';
  rawCols.forEach((rawColName) => {
    const isInConfig = currentConfig.some(c =>
      c.original_name.toLowerCase() === rawColName.toLowerCase() &&
      (c.context === effectiveContext || c.context === 'global')
    );
    if (!isInConfig) {
      newColumnsFound = true;
      currentConfig.push({
        context: effectiveContext,
        original_name: rawColName,
        display_alias: rawColName,
        is_visible: true,
        display_order: 100 + currentConfig.length,
        // Appare in fondo di default
      });
    }
  });
  if (newColumnsFound && setColumnConfig) {
    // Aggiorna lo stato in modo asincrono per evitare loop di rendering
    setTimeout(() => setColumnConfig(currentConfig), 0);
  }
  // Logica per ereditare colonne globali e applicare visibilità/ordine
  const specificCols = currentConfig.filter(c => c.context === effectiveContext);
  const globalCols = currentConfig.filter(c => c.context === 'global');
  const specificOriginalNames = new Set(specificCols.map(c => c.original_name.toLowerCase()));
  const inheritedGlobalCols = globalCols.filter(c => !specificOriginalNames.has(c.original_name.toLowerCase()));
  const finalConfig = [...specificCols, ...inheritedGlobalCols];
  const visibleColsConfig = finalConfig
    .filter(c => c.is_visible && rawCols.some(rc => rc.toLowerCase() === c.original_name.toLowerCase())) // Assicura che la colonna esista nei dati raw
    .sort((a, b) => a.display_order - b.display_order);
  // Ordina
  // Mappa nomi originali (case insensitive) a indici nei dati raw
  const originalNameToIndexMap = {};
  rawCols.forEach((rawColName, index) => {
    originalNameToIndexMap[rawColName.toLowerCase()] = index;
  });
  // Formatta le righe in base alle colonne visibili e ordinate
  const finalRows = table.rows.map(rowArray => {
    return visibleColsConfig.map(configCol => {
      const dataIndex = originalNameToIndexMap[configCol.original_name.toLowerCase()];
      const rawValue = (dataIndex !== undefined) ? rowArray[dataIndex] : null; // Gestisce colonna mancante
      const formattedValue = formatCellForUI(configCol.display_alias, rawValue); // Applica formattazione UI
      return formatCellDisplayValue(formattedValue); // Formattazione finale per display (es. "" per false/0)
    });
  });
  return { columns: visibleColsConfig, rows: finalRows };
}


// Polling (invariato)
let activePollingTasks = {};
const ChatInterface = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [analysisSessionId, setAnalysisSessionId] = useState(null);
  const [chatList, setChatList] = useState([]);
  const [columnConfig, setColumnConfig] = useState([]);
  const [config, setConfig] = useState({ top_k: 5, confirmation_threshold: 0 });
  // threshold 0 = chiedi sempre
  const [configError, setConfigError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState("");
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  // --- INIZIO MODIFICA: Default filtri rapidi su 'tutti' ---
  const [filterVoto, setFilterVoto] = useState(['tutti']);
  const [filterDisponibilita, setFilterDisponibilita] = useState(['tutti']);
  // --- FINE MODIFICA ---
  const [filterLocalita, setFilterLocalita] = useState([]);
  // ---
  const [helpContent, setHelpContent] = useState("");
  const [inputMsg, setInputMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [localitaOptions, setLocalitaOptions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pastedImage, setPastedImage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [oneshotSessionId, setOneshotSessionId] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  const [expandedTables, setExpandedTables] = useState({});
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);

  // --- Change Password State ---
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState("");

  // --- Global Search State ---
  const [activeSidebarTab, setActiveSidebarTab] = useState('chat'); // 'chat' | 'global'
  const [globalFeed, setGlobalFeed] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(false); // True se visualizzo global feed
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);

  // --- Candidate Modal State ---
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidateData, setSelectedCandidateData] = useState({});

  const handleOpenCandidateModal = async (cvid) => {
    if (!cvid) return;
    setSelectedCandidateId(cvid);
    // Fetch candidate data if needed, or just pass ID and let modal fetch?
    // Modal expects candidateData for initial fields.
    // We can fetch it here or let modal handle it if we pass just ID.
    // Current Modal implementation expects candidateData for fields.
    // Let's fetch it here to be safe and consistent.
    try {
      const res = await axios.get(`/api/candidates?search=${encodeURIComponent(cvid)}`);
      if (res.data && res.data.data && res.data.data.length > 0) {
        // Find exact match
        const candidate = res.data.data.find(c => String(c.cvid) === String(cvid));
        if (candidate) {
          setSelectedCandidateData(candidate.metadata_json || {});
          setShowCandidateModal(true);
        }
      }
    } catch (e) {
      console.error("Error fetching candidate for modal:", e);
    }
  };

  const handleSaveCandidate = async (cvid, updatedData) => {
    try {
      await axios.put(`/api/candidates/${cvid}`, { metadata: updatedData });
      setShowCandidateModal(false);
      // Optionally refresh chat messages if they contain old metadata?
      // For now just close.
    } catch (error) {
      console.error("Error updating candidate", error);
      alert("Errore durante l'aggiornamento");
    }
  };

  // --- STATO RICERCA E ORDINAMENTO SIDEBAR ---
  const [chatSearchTerm, setChatSearchTerm] = useState("");
  const [chatSortOrder, setChatSortOrder] = useState("date_desc");

  // --- LOGICA FILTRO E ORDINAMENTO ---
  const filteredAndSortedChats = useMemo(() => {
    let filtered = chatList;

    // Filtro Ricerca
    if (chatSearchTerm.trim()) {
      const term = chatSearchTerm.toLowerCase();
      filtered = filtered.filter(c => (c.title || `Chat ${c.chat_id}`).toLowerCase().includes(term));
    }

    // Ordinamento
    return [...filtered].sort((a, b) => {
      if (chatSortOrder === 'date_desc') {
        return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      } else if (chatSortOrder === 'date_asc') {
        return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
      } else if (chatSortOrder === 'alpha_asc') {
        return (a.title || "").localeCompare(b.title || "");
      } else if (chatSortOrder === 'alpha_desc') {
        return (b.title || "").localeCompare(a.title || "");
      }
      return 0;
    });
  }, [chatList, chatSearchTerm, chatSortOrder]);

  // In frontend/src/App.js

  const generateFilteredPrompt = (basePrompt) => {
    const originalPrompt = basePrompt.trim();
    console.log("generateFilteredPrompt (V5): Prompt originale:", originalPrompt);
    const cleanQueryLower = originalPrompt.toLowerCase();

    // Array di TUTTI i filtri UI potenziali e i loro valori ATTUALI
    const allUiFilters = [
      { key: 'voto', values: filterVoto },
      { key: 'disponibilità', values: filterDisponibilita },
      { key: 'località', values: filterLocalita }
    ];
    // Array dei filtri UI SELEZIONATI (escludendo 'tutti')
    const activeUiFilters = allUiFilters.filter(f => f.values.length > 0 && !(f.values.length === 1 && f.values[0].toLowerCase() === 'tutti'));
    // Determina se il prompt richiede gestione filtri (per pulizia mandatorio vuoto)
    const mandatoryIntents = ['aisafit', 'aisarep', 'aisadeep', 'cerca:'];
    const requiresPotentialMandatorio = mandatoryIntents.some(intent => cleanQueryLower.startsWith(intent)) || cleanQueryLower.includes('mandatorio:');

    // Controlla filtri speciali che bypassano la UI
    const overrideFilters = ['cvid:', 'nominativo:', 'email:', 'e-mail:', 'mostra memoria', 'mostra risultati', 'vedi memoria', 'mostra T-', 'aisafeed:', 'feedback:'];

    // Regex per "Mostra N" (es. Mostra 1, Mostra n. 12) per evitare aggiunta filtri mandatori
    const isNavigationCommand = /^(mostra|vedi|visualizza|fammi vedere)\s*(n\.?|numero)?\s*(\d+)/i.test(cleanQueryLower);

    const hasOverrideFilter = overrideFilters.some(filter => cleanQueryLower.includes(filter.toLowerCase())) || isNavigationCommand;
    if (hasOverrideFilter) {
      console.log("generateFilteredPrompt (V5): Rilevato override filter (o memory command), prompt invariato.");
      return originalPrompt; // Il reset UI è gestito altrove
    }

    // --- NUOVA LOGICA: Gestione "Tutti" ---
    // Verifica se TUTTI i filtri rapidi sono impostati su "Tutti" o sono vuoti
    const allFiltersAreOff = allUiFilters.every(f => f.values.length === 0 || (f.values.length === 1 && f.values[0].toLowerCase() === 'tutti'));
    // Estrae parti del prompt
    let baseQueryPart = originalPrompt;
    let existingMandatorioContent = "";
    const mandatorioRegex = /mandatorio:(.*)/i;
    const mandatorioMatch = originalPrompt.match(mandatorioRegex);

    if (mandatorioMatch) {
      baseQueryPart = originalPrompt.substring(0, mandatorioMatch.index).trim();
      existingMandatorioContent = mandatorioMatch[1].trim().replace(/\.$/, '').trim();
    } else {
      baseQueryPart = originalPrompt;
    }

    // Crea un array di filtri dal contenuto mandatorio esistente
    let existingFiltersArray = [];
    if (existingMandatorioContent) {
      existingFiltersArray = existingMandatorioContent.split(/\s*\.\s*/)
        .map(f => f.trim()).filter(f => f);
    }
    console.log("generateFilteredPrompt (V5): Filtri esistenti (pre-pulizia):", existingFiltersArray);
    // PULIZIA DAI FILTRI UI: Rimuove SEMPRE i filtri rapidi (Voto, Disp, Loc) dall'array esistente
    const quickFilterKeys = ['voto', 'disponibilità', 'località'];
    const cleanedExistingFiltersArray = existingFiltersArray.filter(filterString => {
      const keyMatch = filterString.match(/^([^:]+):/);
      const isQuickFilter = keyMatch && quickFilterKeys.includes(keyMatch[1].trim().toLowerCase());
      if (isQuickFilter) console.log(`generateFilteredPrompt (V5): Rimosso filtro UI esistente: "${filterString}"`);
      return !isQuickFilter; // Mantiene solo i filtri NON UI
    });
    console.log("generateFilteredPrompt (V5): Filtri esistenti (post-pulizia):", cleanedExistingFiltersArray);


    // COSTRUZIONE FILTRI UI: Crea l'array solo se ci sono filtri UI ATTIVI (non "Tutti")
    const uiFiltersToAddArray = activeUiFilters.map(uiFilter => `${uiFilter.key}: ${uiFilter.values.join(', ')}`);
    console.log("generateFilteredPrompt (V5): Filtri UI da aggiungere:", uiFiltersToAddArray);


    // COMBINA: Unisce i filtri esistenti puliti con i NUOVI filtri UI (solo se attivi)
    const finalFiltersArray = [...cleanedExistingFiltersArray, ...uiFiltersToAddArray];
    console.log("generateFilteredPrompt (V5): Filtri finali combinati:", finalFiltersArray);


    // Ricostruisce il prompt finale
    let finalPrompt = baseQueryPart;
    if (finalFiltersArray.length > 0) { // Aggiunge mandatorio SOLO se ci sono filtri finali
      const finalMandatorioString = finalFiltersArray.join('. ');
      // Usa punto come separatore
      finalPrompt += ` mandatorio: ${finalMandatorioString}`;
      // Assicura il punto finale
      if (!finalPrompt.endsWith('.')) {
        finalPrompt += '.';
      }
    } else {
      console.log("generateFilteredPrompt (V5): Nessun filtro finale da aggiungere, sezione mandatorio omessa.");
      // Se non ci sono filtri finali, assicurati che la baseQueryPart non finisca con " mandatorio:" residuo
      finalPrompt = baseQueryPart.replace(/mandatorio:\s*$/i, '').trim();
    }

    // Pulizie finali
    finalPrompt = finalPrompt.replace(/\s{2,}/g, ' ').trim();
    finalPrompt = finalPrompt.replace(/mandatorio:\s*\.$/, '').trim();
    // Rimuove mandatorio vuoto


    console.log("generateFilteredPrompt (V5): Prompt finale generato:", finalPrompt);
    return finalPrompt;
  };
  // --- FINE NUOVA FUNZIONE ---


  // Hook useEffect (invariati)
  useEffect(() => {
    // ... (fetch config colonne e help.txt) ...
    const fetchColumnConfig = async () => {
      try {
        const res = await axios.get(API_URL + "/api/columns/preferences");
        setColumnConfig(res.data);

      } catch (error) {
        console.error("Errore nel caricamento della configurazione colonne:", error);
      }
    };
    fetchColumnConfig();
    axios.get('/help.txt')
      .then(response => setHelpContent(response.data))
      .catch(error =>
        console.error("Errore nel caricamento di help.txt", error));
  }, []);

  const fetchChats = async () => {
    // ... (fetch storico chat) ...
    try {
      const res = await axios.get(API_URL + "/api/history");
      const chatData = res.data || [];
      setChatList(chatData);
      // Logica selezione chat
      if (!selectedChatId && chatData.length > 0) {
        setSelectedChatId(chatData[0].chat_id);
      } else if (selectedChatId && !chatData.some(c => c.chat_id === selectedChatId)) {
        setSelectedChatId(chatData.length > 0 ? chatData[0].chat_id : null);
      } else if (chatData.length === 0) {
        createChat();
        // Crea una chat se non ce ne sono
      }
    } catch (e) {
      console.error("Errore fetch chats:", e);
    }
  }

  const fetchMessages = async (chatId) => {
    // ... (fetch messaggi chat selezionata) ...
    if (!chatId) {
      setMessages([]);
      return;
    }
    try {
      const url = (API_URL ? API_URL : "") + `/api/history/${chatId}`;
      const res = await axios.get(url);
      setMessages(res.data || []);
      // Qui potresti voler reimpostare expandedTables o caricarlo dal backend se persistito
      // setExpandedTables({});
      // Esempio reset
    } catch (e) {
      console.error("Errore fetch messages:", e); setMessages([]);
    }
  }


  const createChat = async () => {
    // ... (crea nuova chat) ...
    try {
      const res = await axios.post(API_URL + "/api/history");
      await fetchChats(); // Ricarica la lista per includere la nuova chat
      setSelectedChatId(res.data.chat_id);
      setActiveSidebarTab('chat');
      setIsReadOnly(false);
      // Seleziona automaticamente la nuova chat
    } catch (e) {
      console.error("Errore creazione chat:", e);
    }
  }

  // --- GLOBAL SEARCH FUNCTIONS ---
  const fetchGlobalFeed = async () => {
    setIsGlobalLoading(true);
    try {
      const res = await axios.get(API_URL + "/api/feed/global/summary");
      setGlobalFeed(res.data || []);
    } catch (e) {
      console.error("Errore fetch global feed:", e);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleTogglePublic = async (msgId, currentStatus) => {
    if (isReadOnly) return; // Non posso modificare se sono in view mode
    const newStatus = !currentStatus;
    try {
      // Optimistic update
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_public: newStatus } : m));

      const chat_id = selectedChatId;
      await axios.post(API_URL + `/api/messages/${msgId}/toggle_public`, { chat_id, is_public: newStatus });

    } catch (e) {
      console.error("Errore toggle public:", e);
      alert("Errore modifica privacy messaggio.");
      // Rollback
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_public: currentStatus } : m));
    }
  };

  const handleDeleteSingle = async (msgId) => {
    if (isReadOnly || !window.confirm("Eliminare solo questo passaggio (Domanda/Risposta)?")) return;
    try {
      await axios.delete(API_URL + `/api/messages/${msgId}/single`, { params: { chat_id: selectedChatId } });
      // Rimuovi dalla UI
      setMessages(prev => {
        const idx = prev.findIndex(m => String(m.id) === String(msgId));
        if (idx === -1) return prev;
        // Rimuovi questo messaggio e il successivo se è assistant
        const next = prev[idx + 1];
        if (next && next.role === 'assistant') {
          return [...prev.slice(0, idx), ...prev.slice(idx + 2)];
        }
        return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });
    } catch (e) {
      console.error("Errore eliminazione singola:", e);
      alert("Errore eliminazione.");
    }
  };

  const handleLoadGlobalItem = (userMsg) => {
    // Trova la risposta associata (messaggio successivo nel feed, o tramite qualche ID?)
    // Il feed è piatto. Cerchiamo il messaggio successivo con role assistant e stesso chat_id e data >
    // Per semplicità, il feed potrebbe ritornare tutto.
    // Costruiamo una "Chat View" virtuale con questi messaggi.

    // Filtriamo dal feed globale tutti i messaggi con stesso chat_id di quello cliccato?
    // O solo la coppia? User request: visualizzare "Globali".
    // Se clicco, voglio vedere quella ricerca.
    // Cerchiamo la risposta nel globalFeed
    const relatedMsgs = globalFeed.filter(m => m.chat_id === userMsg.chat_id);
    // Ordiniamo per data
    relatedMsgs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    setMessages(relatedMsgs);
    setIsReadOnly(true);
    setIsNavOpen(false); // Chiudi sidebar su mobile
    if (window.innerWidth < 768) setIsLeftSidebarCollapsed(true);
  };



  // Effetti per caricamento dati e gestione UI
  useEffect(() => {
    if (user) {
      fetchChats();
    }
  }, [user]); // Aggiunto user come dipendenza per ricaricare al login

  // Carica chat all'inizio (fallback)
  // Carica chat all'inizio (fallback)
  useEffect(() => {
    if (selectedChatId && activeSidebarTab !== 'global') {
      fetchMessages(selectedChatId);
    } else if (!selectedChatId) {
      setMessages([]);
    }
  }, [selectedChatId, activeSidebarTab]);
  // Carica messaggi quando cambia chat
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  // Scrolla alla fine
  useEffect(() => { // Auto-resize textarea
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto'; // Resetta altezza
      textarea.style.height = `${textarea.scrollHeight}px`; // Imposta altezza basata sul contenuto
    }
  }, [inputMsg]);
  // Effetto per polling task asincroni (invariato)
  useEffect(() => {
    const intervalId = setInterval(() => {
      Object.keys(activePollingTasks).forEach(async (taskId) => {
        const task = activePollingTasks[taskId];
        if (!task || task.chatId !== selectedChatId) return; // Controlla anche se task esiste


        try {
          const res = await axios.get(API_URL + `/api/task_status/${taskId}`);
          if (res.data.status === 'SUCCESS' || res.data.status === 'FAILURE') {
            delete activePollingTasks[taskId]; // Rimuove task completato/fallito

            const resultPayload = res.data.result || { answer: `Task ${taskId} fallito o risultato non disponibile.` }; // Fallback
            const finalAssistantMsg = {
              role: 'assistant',

              content: resultPayload.answer,
              table: resultPayload.table,
              context: resultPayload.context,
              message_id: resultPayload.message_id, // 
              // Usa message_id se presente
              id: resultPayload.message_id ||
                `asst-${taskId}` // Usa message_id come ID, fallback con taskId
            };
            // Aggiorna il messaggio "in corso" con il risultato finale
            setMessages(msgs => msgs.map(m => m.id === `thinking-${taskId}` ? finalAssistantMsg : m));
          } else if (res.data.status === 'PENDING' || res.data.status === 'STARTED') {
            // Task ancora in corso, non fare nulla
          }
        } catch (error) {

          console.error(`Errore polling per task ${taskId}:`, error);
          delete activePollingTasks[taskId]; // Rimuove task in errore
          // Aggiorna messaggio "in corso" con messaggio di errore polling
          setMessages(msgs => msgs.map(m => m.id === `thinking-${taskId}` ? { ...m, content: "Errore durante il recupero dello stato del task." } : m));
        }
      });
    }, 3000);
    // Poll ogni 3 secondi
    return () => clearInterval(intervalId);
    // Pulisce intervallo allo smontaggio
  }, [selectedChatId]);
  // Riesegue se cambia la chat selezionata


  // Effetto per caricamento dati iniziali (località, config colonne, help)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Esegue chiamate in parallelo
        await Promise.all([

          axios.get(API_URL + "/api/columns/preferences").then(res => setColumnConfig(res.data)),
          axios.get('/help.txt').then(res => setHelpContent(res.data)),
          axios.get(API_URL + "/api/filters/options/localita").then(res => setLocalitaOptions(res.data || [])),
          // fetchChats() // fetchChats() è già chiamato nel primo useEffect

        ]);
      } catch (error) {
        console.error("Errore nel caricamento dei dati iniziali:", error);
      }
    };
    fetchInitialData();
  }, []);
  // Esegui solo una volta


  // Funzioni gestione tabella espansa (invariate)
  const toggleTableVisibility = (chatId, messageId) => {
    if (!chatId || !messageId) return;
    const key = String(messageId); // Usa sempre stringa come chiave
    setExpandedTables(prev => ({ ...prev, [key]: !prev[key] }));
    // NON salviamo più lo stato espanso nel backend per semplicità
  };

  // --- FUNZIONE sendMessage (MODIFICATA V2 - Logica intent veloci/lenti) ---
  const sendMessage = async (e) => {
    e.preventDefault();
    let userQuery = inputMsg.trim();
    const queryLower = userQuery.toLowerCase(); // Per controlli case-insensitive

    // --- CONTROLLO CONFERMA ---
    const confirmationKeywords = ['confermo', 'procedi', 'si', 'ok', 'continua'];
    const isConfirmation = confirmationKeywords.some(keyword => queryLower.startsWith(keyword));

    // Non inviare se vuoto (a meno che non sia feedback con immagine o conferma)
    if (!userQuery && !pastedImage && !isConfirmation) {
      console.log("sendMessage: Input vuoto e non è conferma/feedback con immagine.");
      return;
    }
    if (!selectedChatId) {
      console.error("Nessuna chat selezionata!");
      return;
    }

    // --- Logica Reset Filtri UI per filtri specifici ---
    const overrideFilters = ['cvid:', 'nominativo:', 'email:', 'e-mail:'];
    const hasOverrideFilter = overrideFilters.some(filter => queryLower.includes(filter));
    const isFollowup = queryLower.startsWith('analizza:');

    if (hasOverrideFilter) {
      console.log("sendMessage: Rilevato filtro anagrafico, resetto i filtri UI.");
      setFilterVoto(['tutti']);
      setFilterDisponibilita(['tutti']);
      setFilterLocalita([]);
      const filtriUIRegex = /(voto:|disponibilità:|località:)\s*[^.]+\.?/gi;
      userQuery = userQuery.replace(filtriUIRegex, '').replace(/\.\s*\./g, '.').replace(/\s{2,}/g, ' ').trim();
    }

    // --- INIZIO MODIFICA: Logica granulare per filtri rapidi ---

    // Rileva l'intent *prima* di applicare i filtri
    const detectedIntent = detectIntent(userQuery);

    // Lista intent "VELOCI" (FTS/SQL) che NON devono avere filtri UI aggiunti
    const fastIntents = ['aisarep', 'cerca:'];

    // Verifica se l'intent rilevato è uno di quelli veloci
    const isFastCommand = fastIntents.some(intent => queryLower.startsWith(intent));

    // Applica i filtri rapidi SOLO se:
    // 1. NON è una conferma
    // 2. NON è un comando veloce (aisarep, cerca:) // <<< RIMUOVIAMO QUESTA CONDIZIONE
    // 3. NON ha un filtro di override (cvid:, email:, ecc.)

    // --- MODIFICA INIZIO ---
    // const shouldApplyFilters = !isConfirmation && !isFastCommand && !hasOverrideFilter; // VECCHIA LOGICA

    // NUOVA LOGICA: Rimuoviamo il blocco !isFastCommand.
    // Ora cerca:/aisarep useranno i filtri UI se attivi.
    const shouldApplyFilters = !isConfirmation && !hasOverrideFilter && !isFollowup;
    // --- MODIFICA FINE ---

    if (shouldApplyFilters) {
      console.log(`sendMessage: Comando lento o NLU generico (Intent: ${detectedIntent}). Applico generateFilteredPrompt.`);
      userQuery = generateFilteredPrompt(userQuery);

      // Pulizia finale (solo se abbiamo applicato i filtri o c'era 'mandatorio:')
      const mandatorioMatchFinal = userQuery.match(/mandatorio:/i);
      if (mandatorioMatchFinal) {
        const mandatorioContentMatchFinal = userQuery.match(/mandatorio:(.*)/i);
        let mandatorioContentFinal = '';
        if (mandatorioContentMatchFinal && mandatorioContentMatchFinal[1]) {
          mandatorioContentFinal = mandatorioContentMatchFinal[1].trim().replace(/\.$/, '').trim();
        }

        if (!mandatorioContentFinal) {
          console.log("sendMessage: Rimuovo mandatorio: vuoto finale.")
          userQuery = userQuery.replace(/\.?\s*mandatorio:\s*\.?$/i, '').trim();
        } else if (!userQuery.trim().endsWith('.')) {
          console.log("sendMessage: Aggiungo punto finale a mandatorio:.");
          userQuery = userQuery.trim() + '.';
        }
      }
    } else if (isConfirmation) {
      console.log("sendMessage: Rilevata conferma, prompt non modificato:", userQuery);
      userQuery = inputMsg.trim(); // Usa il testo originale per la conferma
    } else {
      // Questo copre "isFastCommand" e "chat generica" (come 'ciao')
      console.log(`sendMessage: Comando veloce o chat generica (Intent: ${detectedIntent}). Prompt non modificato.`);
      userQuery = inputMsg.trim(); // Usa il testo originale
    }
    // --- FINE MODIFICA ---


    // Controllo finale: non inviare se la query è diventata vuota dopo la pulizia
    if (!userQuery && !pastedImage) {
      console.log("sendMessage: Query vuota dopo pulizia filtri, annullo invio.");
      setInputMsg(""); // Pulisce input
      return;
    }

    // Crea e invia il messaggio (Logica successiva invariata)
    console.log("sendMessage: Invio prompt finale al backend:", userQuery);
    const newUserMsg = { role: "user", content: userQuery, id: `user-${Date.now()}` };
    setMessages(msgs => [...msgs, newUserMsg]);
    setInputMsg("");
    setIsLoading(true);
    // ... (resto della funzione try/catch/finally per API call) ...
    try {
      // Gestione Feedback
      if (userQuery.toLowerCase().startsWith('aisafeed') && !isConfirmation /* Evita che 'confermo aisafeed' venga trattato come feedback */) {
        // ... logica feedback ...
        const formData = new FormData();
        formData.append('text', userQuery);
        formData.append('chat_id', selectedChatId);
        if (pastedImage) { formData.append('image', pastedImage.file); }

        await axios.post(API_URL + "/api/feedback", formData);
        if (pastedImage) { URL.revokeObjectURL(pastedImage.previewUrl); setPastedImage(null); }
        fetchMessages(selectedChatId);
      } else { // Chiamata API Chat standard (include conferme e comandi normali)
        // Configurazione richiesta
        const config = {
          model: "gpt-4o",
          oneshot_session_id: oneshotSessionId // Invia session ID se presente
        };

        const payload = {
          messages: messages.concat(newUserMsg),
          config: config,
          chat_id: selectedChatId
        };

        // Reset stato file dopo l'invio
        setSelectedFiles([]);
        setOneshotSessionId(null);
        // ... logica analysis_session_id ...
        if (analysisSessionId) {
          payload.analysis_session_id = analysisSessionId;
          setAnalysisSessionId(null);
          setUploadMsg("");
        }

        const res = await axios.post(API_URL + "/api/chat", payload);
        const assistantResponse = res.data;

        // Aggiorna ID messaggio utente con quello reale del DB
        if (assistantResponse.user_message_id) {
          setMessages(msgs => msgs.map(m => m.id === newUserMsg.id ? { ...m, id: assistantResponse.user_message_id, message_id: assistantResponse.user_message_id } : m));
        }

        // ... logica gestione risposta (messaggio, polling) ...
        const newAssistantMsg = {
          role: 'assistant',
          content: assistantResponse.answer || "Risposta non valida dal backend.",
          table: assistantResponse.table,
          context: assistantResponse.context,
          message_id: assistantResponse.message_id,
          id: assistantResponse.message_id || `asst-${Date.now()}`
        };
        if (assistantResponse.action_required?.type === 'poll_task_status') {
          const taskId = assistantResponse.action_required.task_id;
          if (taskId) {
            activePollingTasks[taskId] = { taskId, chatId: selectedChatId };
            newAssistantMsg.id = `thinking-${taskId}`;
            newAssistantMsg.content = assistantResponse.answer || "Ho preso in carico la richiesta. L'analisi è in corso...";
          } else {
            console.error("Azione richiesta 'poll_task_status' ma task_id mancante:", assistantResponse);
            newAssistantMsg.content = "Errore: Avvio task fallito (ID mancante).";
          }
        }
        setMessages(msgs => [...msgs, newAssistantMsg]);
      }
    } catch (e) {
      console.error("Errore invio messaggio:", e);
      setMessages(msgs => [...msgs, { role: "assistant", content: `Si è verificato un errore: ${e.response?.data?.detail || e.message || 'Errore sconosciuto'}` }]);
    } finally {
      setIsLoading(false);
    }
  } // Fine sendMessage

  // Funzioni gestione chat (delete, rename) (invariate)
  const deleteChat = async (chatId) => {
    if (!chatId) return;
    try {
      await axios.delete(API_URL + `/api/history/${chatId}`);
      // Rimuovi ottimisticamente dalla lista
      setChatList(prev => prev.filter(c => c.chat_id !== chatId));

      // Se la chat cancellata era quella selezionata, seleziona la prima disponibile o null
      if (selectedChatId === chatId) {
        const remaining = chatList.filter(c => c.chat_id !== chatId);
        setSelectedChatId(remaining.length > 0 ? remaining[0].chat_id : null);
        if (remaining.length === 0) {
          // Se non ci sono più chat, creane una nuova (opzionale, o lascia vuoto)
          // createChat(); 
        }
      }
    } catch (e) {
      console.error("Errore eliminazione chat:", e);
      alert(`Errore eliminazione chat: ${e.response?.data?.detail || e.message}`);
    }
  }

  const renameChat = async (chatId, newTitle) => {
    if (!chatId || !newTitle || !newTitle.trim()) {
      setEditingChatId(null);
      // Annulla se titolo vuoto
      return;
    };
    try {
      await axios.patch(API_URL + `/api/history/${chatId}`, { title: newTitle.trim() });
      setEditingChatId(null); // Esci da modalità modifica
      fetchChats();
      // Aggiorna titolo nella lista
    } catch (e) {
      console.error("Errore rinomina chat:", e);
    }
  }


  // Gestione File Upload (Nuova Logica)
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setSelectedFiles(files);
    setUploadingFiles(true);

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    try {
      const response = await axios.post(API_URL + '/api/upload_for_session', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setOneshotSessionId(response.data.session_id);
    } catch (error) {
      console.error("Errore upload sessione:", error);
      alert("Errore durante il caricamento dei file.");
      setSelectedFiles([]);
    } finally {
      setUploadingFiles(false);
    }
  };



  // Funzione download (invariata)
  const handleDownload = async (format, tableData, context, query, messageId) => {
    // ... (logica export invariata) ...
    if (!tableData || !tableData.rows || tableData.rows.length === 0) return alert("Nessun dato da esportare.");
    // Usa prepareTableForUI per ottenere colonne e righe formattate come visualizzato
    const uiTable = prepareTableForUI(tableData, context, columnConfig, null);
    // Passa null per non aggiornare lo stato
    if (!uiTable || !uiTable.columns || uiTable.columns.length === 0) return alert("Errore nella preparazione dei dati per l'export.");
    const exportPayload = {
      columns: uiTable.columns.map(c => c.display_alias), // Usa alias display
      rows: uiTable.rows, // Usa righe già formattate
      query: query || `export_${context || 'table'}` // Query fallback
    };
    try {
      const response = await axios.post(`/api/export/${format}`, exportPayload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `report_${messageId || Date.now()}.${format}`; // Nome file più specifico
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url); // Libera memoria
    } catch (error) {
      console.error(`Errore download ${format}:`, error);
      alert(`Impossibile scaricare il file ${format}. ${error.response?.data?.detail || ''}`);
    }
  };

  // --- NUOVE FUNZIONI DOWNLOAD CV ---
  const handleAnonymousDownload = async (cvid, reasoning) => {
    if (!cvid) return;
    try {
      const response = await axios.post(`/api/candidates/${encodeURIComponent(cvid)}/export_company_cv`, { reasoning: reasoning || "" }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Profilo_Anonimo_${cvid}.docx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore download Profilo Anonimo:", error);
      alert("Impossibile scaricare il Profilo Anonimo.");
    }
  };

  const handleSingleDownload = async (cvid) => {
    if (!cvid) return;
    try {
      const response = await axios.get(`/api/download/cv/${cvid}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${cvid}.pdf`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore download CV:", error);
      alert("Impossibile scaricare il CV. File non trovato o errore server.");
    }
  };

  const handleBulkDownload = async (rows, columns) => {
    if (!rows || !rows.length) return alert("Nessun dato da scaricare.");

    const cvidIndex = columns.findIndex(c => c.original_name.toLowerCase() === 'cvid');
    if (cvidIndex === -1) return alert("Colonna CVID non trovata nella tabella.");

    const cvids = rows.map(row => row[cvidIndex]).filter(id => id && id.trim() !== '');

    if (cvids.length === 0) return alert("Nessun CVID valido trovato.");

    try {
      const response = await axios.post('/api/download/zip', { cvids }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `cv_archive_${Date.now()}.zip`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore download ZIP:", error);
      alert("Errore durante la creazione dello ZIP.");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError("");
    setChangePasswordSuccess("");

    try {
      await axios.put(API_URL + "/api/users/me/password", {
        old_password: oldPassword,
        new_password: newPassword
      });
      setChangePasswordSuccess("Password modificata con successo!");
      setOldPassword("");
      setNewPassword("");
      setTimeout(() => {
        setIsChangePasswordOpen(false);
        setChangePasswordSuccess("");
      }, 2000);
    } catch (error) {
      console.error("Errore cambio password:", error);
      setChangePasswordError(error.response?.data?.detail || "Errore durante il cambio password.");
    }
  };


  const handleToggleChatPublic = async (chatId, newStatus) => {
    // Ottimistic Update
    setChatList(currentList => currentList.map(c =>
      c.chat_id === chatId ? { ...c, is_public: newStatus } : c
    ));

    try {
      await axios.post(API_URL + `/api/chats/${chatId}/toggle_public_all`, {
        chat_id: chatId, // Ridondante ma sicuro
        is_public: newStatus
      });
      // Se successo, non facciamo nulla (già aggiornato)
      // Se la chat corrente è quella modificata, ricarichiamo i messaggi per aggiornare le icone singole
      if (chatId === selectedChatId) {
        fetchMessages(chatId);
      }
    } catch (error) {
      console.error("Errore toggle chat public:", error);
      // Rollback su errore
      setChatList(currentList => currentList.map(c =>
        c.chat_id === chatId ? { ...c, is_public: !newStatus } : c
      ));
      alert("Errore nell'aggiornamento dello stato pubblico della chat.");
    }
  };

  // Funzioni azioni messaggio (copy, edit, delete, paste) (invariate)
  const handleCopy = (textToCopy) => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopyFeedback("Copiato!");
      setTimeout(() => setCopyFeedback(""), 1500);
    }).catch(err => console.error("Errore copia:", err));
    // Aggiunto catch
  };

  const handleEdit = (messageIndex) => { // Per modificare messaggio utente
    const messageToEdit = messages[messageIndex];
    // Permette modifica solo se è l'ultimo messaggio e dell'utente
    if (messageToEdit && messageToEdit.role === 'user' && messageIndex === messages.length - 1) {
      setInputMsg(messageToEdit.content);
      // Rimuove il messaggio vecchio per evitare duplicati all'invio
      setMessages(currentMessages => currentMessages.slice(0, messageIndex));
      textareaRef.current?.focus(); // Focus sull'input
    } else if (messageToEdit && messageToEdit.role === 'user') {
      // Se non è l'ultimo, copia solo il testo nell'input
      setInputMsg(messageToEdit.content);
      textareaRef.current?.focus();
    }
  };


  const handleDelete = useCallback(async (messageIndex) => { // Per cancellare da un messaggio in poi
    const messageToDelete = messages[messageIndex];
    // Verifica esistenza e ID (necessario per API backend)
    if (!messageToDelete || (!messageToDelete.id && messageToDelete.id !== 0)) { // ID può essere 0
      console.error("Impossibile eliminare: ID messaggio mancante.", messageToDelete);
      return;

    }

    if (window.confirm("Sei sicuro di voler eliminare questo messaggio e tutte le risposte successive?")) {
      try {
        // Usa message_id se disponibile (per task), altrimenti id
        const idToDelete = messageToDelete.message_id || messageToDelete.id;

        await axios.delete(API_URL + `/api/history/${selectedChatId}/from_message/${idToDelete}`);
        // Aggiorna UI rimuovendo messaggi da index in poi
        setMessages(currentMessages => currentMessages.slice(0, messageIndex));
      } catch (e) {
        console.error("Errore eliminazione messaggio:", e);
        alert(`Errore: ${e.response?.data?.detail || e.message}`); // Mostra errore all'utente
      }
    }
  }, [messages, selectedChatId]);
  // Dipendenze per useCallback


  const handlePaste = (e) => { // Per incollare immagini
    const items = (e.clipboardData || window.clipboardData).items;
    // Compatibilità
    for (const item of items) {
      if (item.type.includes("image")) {
        const file = item.getAsFile();
        if (file) { // Verifica che file esista
          const previewUrl = URL.createObjectURL(file);
          setPastedImage({ file, previewUrl });
          e.preventDefault(); // Impedisce incolla testo immagine
          break;
          // Ferma al primo file immagine
        }
      }
    }
  };

  // JSX RENDER
  return (
    <div className="chat-shell">
      <PortalNavbar section="Chat" />
      <div className="app-container">
      {/* Mobile Nav Toggle */}
      <button className="mobile-nav-toggle" onClick={() => setIsNavOpen(!isNavOpen)}><FaBars /></button>

      {/* Modali */}
      <HelpModal show={showHelp} onClose={() => setShowHelp(false)} content={helpContent} />
      <ColumnConfigurator
        show={showColumnConfig}
        onClose={() => setShowColumnConfig(false)}
        initialConfig={columnConfig}
        isSaving={isSavingConfig}
        error={configError}
        onSave={(newConfig) => {
          setIsSavingConfig(true); setConfigError('');
          axios.post(API_URL + '/api/columns/preferences', newConfig)

            .then(() => { setColumnConfig(newConfig); setShowColumnConfig(false); })
            .catch(err => { console.error("Errore salvataggio config colonne", err); setConfigError("Salvataggio fallito."); })
            .finally(() => setIsSavingConfig(false));
        }}
      />

      {/* Sidebar Nav (Storico Chat) */}
      <div className={`sidebar-nav ${isNavOpen ? 'open' : ''} ${isLeftSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {!isLeftSidebarCollapsed && <div className="sidebar-title">Storico chat</div>}
          <button className="mobile-close-btn" onClick={() => setIsNavOpen(false)}>&times;</button>
          <button className="collapse-btn desktop-only" onClick={() => setIsLeftSidebarCollapsed(!isLeftSidebarCollapsed)}>
            {isLeftSidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>
        <div className={`sidebar-content ${isLeftSidebarCollapsed ? 'hidden' : ''}`}>
          <div className="sidebar-tabs" style={{ display: 'flex', padding: '0 10px', marginBottom: '10px', gap: '5px' }}>
            <button
              className={`tab-btn ${activeSidebarTab === 'chat' ? 'active' : ''}`}
              onClick={() => { setActiveSidebarTab('chat'); setIsReadOnly(false); fetchChats(); }}
            >
              Mie Chat
            </button>
            <button
              className={`tab-btn ${activeSidebarTab === 'global' ? 'active' : ''}`}
              onClick={() => { setActiveSidebarTab('global'); fetchGlobalFeed(); }}
            >
              Globali
            </button>
          </div>

          {activeSidebarTab === 'chat' ? (
            <>
              <button className="new-chat-btn btn-primary" onClick={createChat} disabled={isLoading}>+ Nuova chat</button>
              <button className="new-chat-btn" onClick={() => navigate('/metadata')} style={{ marginTop: '0.5rem' }}>Gestione Candidati</button>
              {['admin', 'editor', 'viewer'].includes(user?.role) && (
                <button className="new-chat-btn" onClick={() => navigate('/dashboard')} style={{ marginTop: '0.5rem' }}>Dashboard</button>
              )}
              {user?.role === 'admin' && (
                <button className="new-chat-btn" onClick={() => navigate('/users')} style={{ marginTop: '0.5rem' }}>Gestione Utenti</button>
              )}


              {/* --- UI RICERCA E ORDINAMENTO --- */}
              <div style={{ padding: '0 10px 10px 10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <input
                  type="text"
                  placeholder="Cerca chat..."
                  value={chatSearchTerm}
                  onChange={(e) => setChatSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '0.9rem'
                  }}
                />
                <select
                  value={chatSortOrder}
                  onChange={(e) => setChatSortOrder(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '4px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="date_desc">📅 Più recenti</option>
                  <option value="date_asc">📅 Meno recenti</option>
                  <option value="alpha_asc">🔤 A-Z</option>
                  <option value="alpha_desc">🔤 Z-A</option>
                </select>
              </div>

              <div className="chat-list">
                {filteredAndSortedChats.map((c) => (

                  <div key={c.chat_id} className={`chat-list-item ${c.chat_id === selectedChatId && !isReadOnly ? 'selected' : ''}`} onClick={() => { if (!isLoading) { setSelectedChatId(c.chat_id); setIsReadOnly(false); setIsNavOpen(false); fetchMessages(c.chat_id); } }}>
                    {editingChatId === c.chat_id ? (
                      <input className="chat-rename-input" value={editingTitle} autoFocus onChange={e => setEditingTitle(e.target.value)} onBlur={() => renameChat(c.chat_id, editingTitle)} onKeyDown={e => { if (e.key === "Enter") renameChat(c.chat_id, editingTitle); }} />

                    ) : (
                      <span className="chat-title">{c.title || `Chat ${c.chat_id.substring(0, 4)}`}</span> // Fallback titolo
                    )}
                    {/* Azioni chat solo se non in modifica */}
                    {editingChatId !== c.chat_id && (
                      <>

                        <button className="chat-action-btn" title={c.is_public ? "Rendi Privata" : "Rendi Chat Globale"} onClick={e => { e.stopPropagation(); handleToggleChatPublic(c.chat_id, !c.is_public); }} style={{ color: c.is_public ? '#28a745' : '#ccc' }}>
                          <FaGlobe />
                        </button>
                        <span className="chat-action-btn" title="Rinomina" onClick={e => { e.stopPropagation(); setEditingChatId(c.chat_id); setEditingTitle(c.title || `Chat ${c.chat_id.substring(0, 4)}`); }}>✏️</span>
                        <span className="chat-action-btn" title="Elimina" onClick={e => { e.stopPropagation(); if (window.confirm(`Eliminare "${c.title || 'questa chat'}"?`)) deleteChat(c.chat_id); }}>🗑️</span>
                      </>
                    )}
                  </div>

                ))}
              </div>
            </>
          ) : (
            <div className="chat-list global-list">
              {isGlobalLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <div className="typing-indicator" style={{ display: 'inline-block', marginBottom: '10px' }}>
                    <span>.</span><span>.</span><span>.</span>
                  </div>
                  <div>Caricamento feed globale...</div>
                </div>
              ) : (
                <>
                  {globalFeed.map(chat => (
                    <div key={chat.chat_id} className={`chat-list-item ${chat.chat_id === selectedChatId && isReadOnly ? 'selected' : ''}`} onClick={() => {
                      setSelectedChatId(chat.chat_id);
                      setIsReadOnly(true);
                      setIsNavOpen(false);
                      // Carica i dettagli della chat
                      fetchMessages(chat.chat_id);
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#333' }}>
                          {chat.username} - {chat.chat_title || "Chat senza titolo"}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {prettifyDataItaliana(chat.updated_at)} • {chat.message_count} {chat.message_count === 1 ? 'msg' : 'msgs'}
                        </div>
                      </div>
                    </div>
                  ))}
                  {globalFeed.length === 0 && <div style={{ padding: '10px', color: '#777' }}>Nessuna ricerca pubblica.</div>}
                </>
              )}
            </div>
          )}
        </div>
      </div >

      {/* Overlay per chiudere sidebar mobile */}
      {isNavOpen && <div className="overlay" onClick={() => setIsNavOpen(false)}></div>}

      {/* Area Chat Principale */}
      <div className="main-chat">
        {/* Header Mobile */}

        <div className="mobile-header">
          <button className="menu-toggle-btn" onClick={() => setIsNavOpen(true)}><FaBars /></button>
          <div className="mobile-header-title">TalentGPT</div>
          <button className="menu-toggle-btn" onClick={() => setIsConfigOpen(!isConfigOpen)}>⚙</button> {/* Toggle Config */}
        </div>

        {/* Area Messaggi */}

        <div className="messages-area">
          {copyFeedback && <div className="copy-feedback">{copyFeedback}</div>}
          {messages.map((msg, i) => {
            const messageId = String(msg.id ?? msg.message_id ?? `msg-${i}`); // ID robusto
            const hasTable = msg.table && msg.table.rows && msg.table.rows.length > 0;
            // Prepara tabella SOLO se necessario (ottimizzazione)
            const uiTable = hasTable ?
              prepareTableForUI(msg.table, msg.context, columnConfig, setColumnConfig) : null;

            // --- INIEZIONE BOTTONI DOWNLOAD (Fixed: Usa dati raw per trovare CVID anche se nascosto) ---
            let tableRowIds = null;
            if (uiTable && uiTable.rows && uiTable.columns && msg.table && msg.table.columns) {
              const rawCvidIndex = msg.table.columns.findIndex(c => c.toLowerCase() === 'cvid');

              if (rawCvidIndex !== -1) {
                // Estrae gli ID grezzi per AisaTable (fondamentale per selezione multipla corretta)
                tableRowIds = msg.table.rows.map(r => r[rawCvidIndex]);

                // Aggiungi colonna Azioni
                uiTable.columns.push({
                  original_name: 'azioni',
                  display_alias: 'Azioni',
                  is_visible: true,
                  widthClass: 'col-narrow'
                });

                // Aggiungi bottone a ogni riga usando l'indice raw
                uiTable.rows = uiTable.rows.map((row, rowIndex) => {
                  // Recupera il CVID dai dati RAW usando l'indice corretto
                  const cvid = msg.table.rows[rowIndex] ? msg.table.rows[rowIndex][rawCvidIndex] : null;

                  const actionCell = (
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        className="download-icon-btn"
                        onClick={() => handleSingleDownload(cvid)}
                        title="Scarica CV originale"
                        disabled={!cvid}
                      >
                        ⬇️
                      </button>
                      <button
                        className="download-icon-btn"
                        onClick={() => {
                          // Tenta di recuperare il ragionamento dalla riga
                          // Cerca indice colonna Ragionamento o Reasoning
                          const reasonIdx = msg.table.columns.findIndex(c => c.toLowerCase().includes('ragionamento') || c.toLowerCase().includes('reasoning'));
                          const reason = (reasonIdx !== -1 && msg.table.rows[rowIndex]) ? msg.table.rows[rowIndex][reasonIdx] : "";
                          handleAnonymousDownload(cvid, reason);
                        }}
                        title="Scarica Profilo Anonimo"
                        disabled={!cvid}
                      >
                        📄
                      </button>
                      {!msg.table.disable_candidate_management && (
                        <button
                          className="download-icon-btn"
                          onClick={() => handleOpenCandidateModal(cvid)}
                          title="Vedi Profilo"
                          disabled={!cvid}
                        >
                          👤
                        </button>
                      )}
                    </div>
                  );
                  return [...row, actionCell];
                });
              }
            }
            // --- FINE INIEZIONE ---

            const isTableExpanded = expandedTables[messageId];
            return (
              <div key={messageId} className={`message-wrapper ${msg.role} ${msg.is_public ? 'public-message' : ''}`}>
                <div className="message-bubble">
                  {/* Header Messaggio (Date - Username - Global Toggle) */}
                  <div className="message-header" style={{
                    fontSize: '0.75rem',
                    marginBottom: '4px',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span>
                      {prettifyDataItaliana(msg.created_at)}
                      {msg.username ? ` - ${msg.username}` : ''}
                    </span>
                    {/* Toggle Global (Solo per User e se non ReadOnly e Owner check implicit) */}
                    {msg.role === 'user' && !isReadOnly && (
                      <span
                        title={msg.is_public ? "Pubblica (Visibile a tutti)" : "Privata (Clicca per rendere pubblica)"}
                        onClick={() => handleTogglePublic(msg.id, msg.is_public)}
                        style={{ cursor: 'pointer', marginLeft: '8px', color: msg.is_public ? 'green' : '#ccc' }}
                      >
                        <FaGlobe />
                      </span>
                    )}
                    {(isReadOnly && msg.role === 'user' && msg.is_public) && <span title="Pubblica" style={{ color: 'green', marginLeft: '8px' }}><FaGlobe /></span>}
                  </div>

                  {/* Contenuto messaggio */}
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>

                  {/* Gestione Tabella */}
                  {hasTable && uiTable && (
                    <div className="table-container">
                      <button onClick={() => toggleTableVisibility(selectedChatId, messageId)} className="show-details-btn">

                        {isTableExpanded ? 'Nascondi Dettagli' : 'Mostra Dettagli'} ({uiTable.rows.length} candidati)
                      </button>
                      {isTableExpanded && (
                        <div className="table-scroll-wrapper">

                          <AisaTable
                            columns={uiTable.columns}
                            data={uiTable.rows}
                            rowIds={tableRowIds}
                            footerContent={
                              <div className="export-buttons" style={{ marginTop: 0 }}> {/* Style reset since inside sticky footer */}
                                <span>Report:</span>
                                <button onClick={() => handleDownload('docx', msg.table, msg.context, msg.content, messageId)}>DOCX</button>
                                <button onClick={() => handleDownload('pdf', msg.table, msg.context, msg.content, messageId)}>PDF</button>
                              </div>
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}

                </div>
                {/* Azioni Messaggio */}
                <div className="message-actions">
                  <button title="Copia" onClick={() => handleCopy(msg.content)}>📋</button>
                  {/* Modifica/Elimina solo per messaggi utente */}

                  {msg.role === 'user' && !isReadOnly && (
                    <>
                      {/* Permetti modifica solo dell'ultimo messaggio */}
                      {i === messages.length
                        - 1 && <button title="Modifica" onClick={() => handleEdit(i)}>✏️</button>}
                      {/* Copia testo se non è l'ultimo */}
                      {i !== messages.length - 1 && <button title="Copia nell'input" onClick={() => handleEdit(i)}>⤴️</button>}

                      <button title="Elimina solo questo scambio" onClick={() => handleDeleteSingle(msg.id)}><FaTimes /></button>
                      <button title="Elimina da qui in poi" onClick={() => handleDelete(i)}>🗑️</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {/* Indicatore "sta scrivendo" (semplice) */}
          {isLoading && <div className="message-wrapper assistant"><div className="message-bubble typing-indicator"><span>.</span><span>.</span><span>.</span></div></div>}
          {/* Riferimento per scroll */}
          <div ref={messagesEndRef} />
        </div>

        {/* Form Input Messaggio */}

        <div className="input-area">
          <div className="input-wrapper">
            <textarea
              className="input-textarea"
              ref={textareaRef}
              value={inputMsg}
              onChange={(e) => setInputMsg(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder="Scrivi qui..."
              rows={1}
              onPaste={handlePaste}
            />
            <button onClick={sendMessage} disabled={isLoading || (!inputMsg.trim() && !pastedImage)} className="submit-button">
              {isLoading ? '...' : 'Invia'}
            </button>
          </div>

          {/* File Upload Section */}
          <div className="file-upload-section" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload" className="new-chat-btn btn-analyze" style={{ width: 'auto', display: 'inline-block', margin: 0 }}>
              📎 Analizza CVs
            </label>
            {selectedFiles.length > 0 && (
              <span style={{ fontSize: '0.9rem' }}>
                {selectedFiles.length} file selezionati
                {uploadingFiles && " (Caricamento...)"}
                {!uploadingFiles && oneshotSessionId && " ✅ Pronti"}
              </span>
            )}
          </div>
          {pastedImage && (
            <div className="pasted-image-preview">
              <img src={pastedImage.previewUrl} alt="Incolla" />
              <button onClick={() => setPastedImage(null)}>×</button>
            </div>
          )}
        </div>

      </div> {/* Close main-chat */}

      {/* Sidebar Configurazione */}
      <div className={`sidebar-config ${isConfigOpen ? 'open' : ''} ${isRightSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {!isRightSidebarCollapsed && <div className="sidebar-title">Configurazione</div>}
          <button className="mobile-close-btn" onClick={() => setIsConfigOpen(false)}>&times;</button>
          <button className="collapse-btn desktop-only" onClick={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}>
            {isRightSidebarCollapsed ? <FaChevronLeft /> : <FaChevronRight />}
          </button>
        </div>
        <div className={`sidebar-content ${isRightSidebarCollapsed ? 'hidden' : ''}`}>
          {/* Filtri Rapidi */}
          <div className="config-section">
            <div className="config-label">Filtri Rapidi</div>

            {/* Voto */}
            <label htmlFor="voto-filter">Voto:</label>
            <select id="voto-filter" className="config-select" multiple value={filterVoto} onChange={e => setFilterVoto(Array.from(e.target.selectedOptions, option => option.value))}>
              <option value="tutti"></option>
              <option value="top">Top</option>
              <option value="ottimo">Ottimo</option>
              <option value="molto buono">Molto Buono</option>
              <option value="buono">Buono</option>
              <option value="off">Off - tutti i candidati</option>
            </select>
            {/* Disponibilità */}
            <label htmlFor="disponibilita-filter">Disponibilità (data aggiorn.):</label>
            <select
              id="disponibilita-filter" className="config-select" multiple value={filterDisponibilita} onChange={e => setFilterDisponibilita(Array.from(e.target.selectedOptions, option => option.value))}>
              <option value="tutti"></option>
              <option value="ultimo trimestre">Ultimo Trimestre</option>
              <option value="ultimo semestre">Ultimo Semestre</option>
              <option value="ultimo anno">Ultimo Anno</option>
              <option value="ultimo biennio">Ultimo Biennio</option>
              <option value="off">Off - tutti i candidati</option>
            </select>
            {/* Località */}
            <label htmlFor="localita-filter">Località:</label>
            <select id="localita-filter" className="config-select" multiple value={filterLocalita} onChange={e => setFilterLocalita(Array.from(e.target.selectedOptions, option => option.value))}>
              {/* Opzione vuota 
                per deselezionare */}
              <option value="">-- Seleziona --</option>
              {localitaOptions.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          </div>
          {/* Bottoni Gestione Colonne e Guida */}
          <button onClick={() => setShowColumnConfig(true)} className="new-chat-btn">Gestisci
            Colonne Tabella</button>
          <button onClick={() => setShowHelp(true)} className="new-chat-btn">Guida Rapida</button>
          <button onClick={() => setIsChangePasswordOpen(true)} className="new-chat-btn btn-danger" style={{ marginTop: '10px' }}>Cambio Password</button>
          <button onClick={logout} className="new-chat-btn btn-danger" style={{ marginTop: '10px' }}>Logout ({user?.username})</button>
        </div>
      </div>

      {/* Modal Cambio Password */}
      {
        isChangePasswordOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Cambia Password</h3>
              {changePasswordError && <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>{changePasswordError}</div>}
              {changePasswordSuccess && <div className="success-message" style={{ color: 'green', marginBottom: '10px' }}>{changePasswordSuccess}</div>}
              <form onSubmit={handleChangePassword}>
                <div className="form-group">
                  <label>Vecchia Password:</label>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                    className="modal-input"
                    style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
                  />
                </div>
                <div className="form-group">
                  <label>Nuova Password:</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="modal-input"
                    style={{ width: '100%', padding: '8px', marginBottom: '20px' }}
                  />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn-primary">Salva</button>
                  <button type="button" onClick={() => setIsChangePasswordOpen(false)} className="btn-secondary">Annulla</button>
                </div>
              </form>
            </div>
          </div>
        )
      }


      {/* Divisore */}

      {/* Candidate Edit Modal */}
      <CandidateEditModal
        show={showCandidateModal}
        onClose={() => setShowCandidateModal(false)}
        candidateId={selectedCandidateId}
        candidateData={selectedCandidateData}
        onSave={handleSaveCandidate}
      />

      </div >
    </div>
  );
}
export default ChatInterface;
